<script setup>
import { ref, computed, defineAsyncComponent, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { TOPICS, RESOURCE_TYPES, getLabel } from '@/utils/resourceTags.js'
import { formatMonthYear } from '@/utils/formatDate.js'
import { useCmsUiVisible } from '@/composables/useCmsUiVisible.js'
import { isReviewLive } from '@/cms/reviewGate.js'
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_TYPE,
  DEFAULT_OG_IMAGE_ALT,
  withTrailingSlash
} from '@/utils/siteUrl'
import OurWorkCard from '@/components/OurWorkCard.vue'
import Breadcrumbs from '@/components/Breadcrumbs.vue'

const isCmsEnabled = import.meta.env.MODE === 'cms'
const CmsEditorModal = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsEditorModal.vue'))
  : null

const { cmsUiVisible } = useCmsUiVisible()
const showCmsChrome = computed(() => isCmsEnabled && cmsUiVisible.value)

const route = useRoute()
const router = useRouter()

const modules = import.meta.glob('/src/content/resources/*.md', { eager: true })
const ourWorkModules = import.meta.glob('/src/content/our-work/*.md', { eager: true })
const slug        = computed(() => route.params.slug)
const contentPath = computed(() => `/src/content/resources/${slug.value}.md`)
const isEditing   = ref(false)

const frontmatter = computed(() => {
  const mod = modules[contentPath.value]
  if (!mod) return null
  return {
    title:       mod.title,
    description: mod.description,
    type:        mod.type,
    topics:      mod.topics,
    thumbnail:   mod.thumbnail,
    publishedOn: mod.publishedOn || null,
    publishedAt: mod.publishedAt || null,
    reviewStatus: mod.reviewStatus || '',
    displayDate: mod.publishedOn || mod.publishedAt || null,
    downloads:   (mod.downloads || []).map(d => ({ label: d.label, file: d.file, size: d.size || '' })),
    authors:     (mod.authors || []).map(a => ({ name: a.name, organisation: a.organisation || '' })),
    citation:    mod.citation || '',
    projectId:   mod.projectId || '',
    noDetailPage: !!mod.noDetailPage,
  }
})

// Some resources are external-link-only and must never render their own detail
// page (e.g. mind-gap.md) — bounce straight back to the listing.
watchEffect(() => {
  if (frontmatter.value?.noDetailPage) router.replace('/resources')
})

const relatedProjects = computed(() => {
  const id = frontmatter.value?.projectId
  if (!id) return []
  return Object.entries(ourWorkModules)
    .filter(([path, mod]) => mod.projectId === id && !path.endsWith('/README.md'))
    .map(([path, mod]) => ({
      slug:    path.replace('/src/content/our-work/', '').replace('.md', ''),
      title:   mod.title,
      excerpt: mod.excerpt,
      image:   mod.image || '',
    }))
})

const isPublished = computed(() => {
  if (!frontmatter.value) return true
  if (import.meta.env.DEV) return true
  const p = frontmatter.value.publishedAt
  return (!p || new Date(p) <= new Date()) && isReviewLive(frontmatter.value.reviewStatus)
})

const breadcrumbs = computed(() => [
  { label: 'Home', to: '/' },
  { label: 'Resources', to: '/resources' },
  { label: frontmatter.value?.title || 'Resource' },
])

const baseUrl = import.meta.env.VITE_APP_BASE_URL || SITE_URL

// Sitewide default share image (jk-35) unless this resource sets its own thumbnail.
useHead(computed(() => {
  const fm = frontmatter.value
  const title = fm ? fm.title : 'Resource'
  const description = fm?.description || ''
  const url = `${baseUrl}${withTrailingSlash(`/resources/${slug.value}`)}`
  const hasCustomImage = Boolean(fm?.thumbnail)
  const ogImage = fm?.thumbnail || DEFAULT_OG_IMAGE
  const ogImageAbs = `${baseUrl}${ogImage}`
  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: url },
      { property: 'og:image', content: ogImageAbs },
      { name: 'twitter:image', content: ogImageAbs },
      // Known dimensions only apply to the default image — a resource's own
      // thumbnail may be a different size, so we don't claim to know it.
      ...(hasCustomImage ? [] : [
        { property: 'og:image:width', content: DEFAULT_OG_IMAGE_WIDTH },
        { property: 'og:image:height', content: DEFAULT_OG_IMAGE_HEIGHT },
        { property: 'og:image:type', content: DEFAULT_OG_IMAGE_TYPE },
        { property: 'og:image:alt', content: DEFAULT_OG_IMAGE_ALT }
      ]),
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
    link: [{ rel: 'canonical', href: url }],
  }
}))

const lazyModules  = import.meta.glob('/src/content/resources/*.md')
const MarkdownBody = computed(() => {
  const key = `/src/content/resources/${slug.value}.md`
  if (!lazyModules[key]) return null
  return defineAsyncComponent(lazyModules[key])
})
</script>

<template>
  <div class="resource-detail container">
    <template v-if="!frontmatter">
      <h1>Resource not found</h1>
      <RouterLink to="/resources/">← Back to resources</RouterLink>
    </template>

    <template v-else-if="!isPublished">
      <p class="not-published">This resource is not yet available.</p>
      <div class="back">
        <RouterLink to="/resources/">← Back to resources</RouterLink>
      </div>
    </template>

    <template v-else>
      <Breadcrumbs :items="breadcrumbs" />

      <div class="meta">
        <span class="tag type">{{ getLabel(RESOURCE_TYPES, frontmatter.type) }}</span>
        <span v-for="topic in (frontmatter.topics || [])" :key="topic" class="tag topic">
          {{ getLabel(TOPICS, topic) }}
        </span>
        <time v-if="frontmatter.displayDate" class="published" :datetime="frontmatter.displayDate">Published {{ formatMonthYear(frontmatter.displayDate) }}</time>
        <button v-if="showCmsChrome" class="edit-inline" @click="isEditing = true">Edit</button>
      </div>
      <CmsEditorModal v-if="isCmsEnabled && isEditing" :content-path="contentPath" @close="isEditing = false" />

      <component :is="MarkdownBody" v-if="MarkdownBody" class="body" />

      <div v-if="frontmatter.downloads?.length" class="downloads">
        <h2>Downloads</h2>
        <div class="download-list">
          <a
            v-for="d in frontmatter.downloads"
            :key="d.file"
            :href="d.file"
            download
            class="download-item"
          >
            <span class="icon">↓</span>
            <span>{{ d.label }}<template v-if="d.size"> ({{ d.size }})</template></span>
          </a>
        </div>
      </div>

      <div v-if="frontmatter.authors?.length" class="authors">
        <h2>Authors</h2>
        <ul class="author-list">
          <li v-for="a in frontmatter.authors" :key="a.name">
            <span class="name">{{ a.name }}</span>
            <span v-if="a.organisation" class="org">{{ a.organisation }}</span>
          </li>
        </ul>
      </div>

      <div v-if="frontmatter.citation" class="citation">
        <h2>Citation</h2>
        <p>{{ frontmatter.citation }}</p>
      </div>

      <div v-if="relatedProjects.length" class="related-project">
        <h2>Related project</h2>
        <div class="cards">
          <OurWorkCard
            v-for="project in relatedProjects"
            :key="project.slug"
            :slug="project.slug"
            :title="project.title"
            :excerpt="project.excerpt"
            :image="project.image"
          />
        </div>
      </div>

      <div class="back">
        <RouterLink to="/resources/">← Back to resources</RouterLink>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.resource-detail {
  max-width: 760px;
  padding-block: $space-8;

  .breadcrumbs { margin-bottom: $space-5; }

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $space-2;
    margin-bottom: $space-6;

    .published {
      margin-left: auto;
      font-size: $font-size-cap;
      color: var(--color-text-muted);
      white-space: nowrap;
    }

    .edit-inline {
      margin-left: auto;
      background: none;
      border: 1px solid var(--color-border);
      border-radius: $radius-sm;
      color: var(--color-text-muted);
      cursor: pointer;
      font-family: $font-sans;
      font-size: $font-size-cap;
      font-weight: 600;
      padding: 2px $space-2;
      @include transition(color, border-color, background);

      &:hover         { color: var(--color-primary); border-color: var(--color-primary); }
      &:focus-visible { @include focus-ring; }
      &:active        { transform: translateY(1px); }
    }
  }

  .body {
    line-height: $lh-base;
    color: var(--color-text);
  }

  .downloads {
    margin-top: $space-6;
    padding-top: $space-6;
    border-top: 1px solid var(--color-border);

    h2 {
      font-size: $font-size-lg;
      font-weight: 600;
      color: var(--color-ink);
      margin-bottom: $space-4;
    }

    .download-list {
      display: flex;
      flex-direction: column;
      gap: $space-2;
    }

    .download-item {
      display: flex;
      align-items: center;
      gap: $space-3;
      padding: $space-3 $space-4;
      background: var(--color-fill-subtle);
      border: 1px solid var(--color-border);
      border-radius: $radius-sm;
      text-decoration: none;
      font-size: $font-size-sm;
      color: var(--color-text);
      @include transition(background, border-color);

      &:hover {
        background: var(--color-primary-tint);
        border-color: var(--color-primary);
        color: var(--color-primary);
      }

      .icon {
        font-size: $font-size-lg;
        color: var(--color-primary);
        line-height: 1;
      }
    }
  }

  .authors,
  .citation {
    margin-top: $space-6;
    padding-top: $space-6;
    border-top: 1px solid $color-border;

    h2 {
      font-size: $font-size-lg;
      font-weight: 600;
      color: $color-ink;
      margin-bottom: $space-4;
    }
  }

  .related-project {
    margin-top: $space-6;
    padding-top: $space-6;
    border-top: 1px solid $color-border;

    h2 {
      font-size: $font-size-lg;
      font-weight: 600;
      color: $color-ink;
      margin-bottom: $space-4;
    }

    .cards {
      display: flex;
      flex-direction: column;
      gap: $space-4;
    }
  }

  .author-list {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: $space-2;
      font-size: $font-size-sm;
    }

    .name {
      font-weight: 600;
      color: $color-ink;
    }

    .org {
      color: $color-text-muted;
    }
  }

  .citation p {
    font-size: $font-size-sm;
    line-height: $lh-base;
    color: $color-text;
  }

  .not-published {
    padding: $space-10 0;
    color: var(--color-text-muted);
    font-style: italic;
  }

  .back {
    margin-top: $space-8;
    padding-top: $space-5;
    border-top: 1px solid var(--color-border);
    font-size: $font-size-sm;

    a { color: var(--color-primary); text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
